import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import UserAvatar from "../Common/UserAvatar";
import { Card, Row, Col, Button } from "react-bootstrap";
import Select from "react-select";
import PhoneInputWithCountry from "../Common/CountryCodeSelect";

const WHATSAPP_TRIGGERS = [
    { key: "DPR", label: "DPR" },
    { key: "JMR", label: "JMR" },
    { key: "MIR", label: "MIR" },
    { key: "WIR", label: "WIR" },
    { key: "Drawings", label: "Drawings" },
    { key: "Billing", label: "Billing" },
    { key: "Indent", label: "Indent" },
    { key: "WorkOrder", label: "Work Order" },
    { key: "PurchaseOrder", label: "Purchase Order" },
    { key: "Delivery", label: "Delivery" },
    { key: "Milestone", label: "Milestone" },
    { key: "Handover", label: "Handover" },
    { key: "QCInspection", label: "QC Inspection" },
    { key: "TestCertificate", label: "Test Certificate" },
    { key: "StatutoryApproval", label: "Statutory Approval" },
    { key: "SafetyIncident", label: "Safety Incident" },
];

const AddClient = () => {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [form, setForm] = useState({
        title: "",
        countryCode: "+91",
        mobile: "",
        gmail: "",
        billingAddress: { addressLine1: "", city: "", state: "", pincode: "" },
        shippingAddress: { addressLine1: "", city: "", state: "", pincode: "" },
        gstDetails: {
            gstNumber: "",
            panNumber: "",
            cinNumber: "",
        },
        contactPersons: [
            {
                name: "",
                countryCode: "+91",
                mobile: "",
                email: "",
                designation: "",
                whatsappTriggers: [],
            },
        ],
    });
    // =========================
    // REGEX VALIDATION RULES
    // =========================
    const mobileRegex = /^(?:\+?\d{1,3}[- ]?)?[6-9]\d{9}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const gstRegex =
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    const cinRegex = /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;

    const pincodeRegex = /^[1-9][0-9]{5}$/;
    /* =========================
    SIMPLE VALIDATION
    // ========================= */
    const validateForm = () => {
        if (!form.title) return "Client / Company name is required";

        const digits = form.mobile.replace(/\D/g, "");

        if (digits.length < 10)
            return "Mobile number must contain at least 10 digits";

        if (!emailRegex.test(form.gmail)) return "Invalid email address";

        const b = form.billingAddress;
        if (!b.addressLine1 || !b.city || !b.state || !pincodeRegex.test(b.pincode))
            return "Complete valid billing address is required";

        const s = form.shippingAddress;
        if (!s.addressLine1 || !s.city || !s.state || !pincodeRegex.test(s.pincode))
            return "Complete valid shipping address is required";

        if (!form.gstDetails.gstNumber) return "GST Number is required";

        if (!gstRegex.test(form.gstDetails.gstNumber)) return "Invalid GST Number";

        if (form.gstDetails.panNumber && !panRegex.test(form.gstDetails.panNumber))
            return "Invalid PAN Number";

        if (form.gstDetails.cinNumber && !cinRegex.test(form.gstDetails.cinNumber))
            return "Invalid CIN Number";

        return null;
    };

    /* =========================
    CHANGE HANDLER
    // ========================= */
    const handleChange = (e) => {
        let { name, value } = e.target;

        if (
            name.includes("gstNumber") ||
            name.includes("panNumber") ||
            name.includes("cinNumber")
        ) {
            value = value.toUpperCase();
        }

        if (name.includes(".")) {
            const [parent, child] = name.split(".");

            setForm((prev) => {
                const updatedParent = {
                    ...prev[parent],
                    [child]: value,
                };

                const updatedForm = {
                    ...prev,
                    [parent]: updatedParent,
                };

                // [OK] LIVE SYNC billing → shipping
                if (sameAsBilling && parent === "billingAddress") {
                    updatedForm.shippingAddress = { ...updatedParent };
                }

                return updatedForm;
            });
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    /* =========================
    SUBMIT
    // ========================= */
    const handleSubmit = async (e) => {
        e.preventDefault();
        const error = validateForm();
        if (error) {
            return swal("Validation Error", error, "error");
        }
        // FORCE SYNC before validation
        const finalForm = {
            ...form,
            shippingAddress: sameAsBilling
                ? { ...form.billingAddress }
                : form.shippingAddress,
        };

        const b = finalForm.billingAddress;
        const s = finalForm.shippingAddress;

        if (!b.addressLine1 || !b.city || !b.state || !b.pincode) {
            return swal("Error", "Complete billing address is required", "error");
        }

        if (!s.addressLine1 || !s.city || !s.state || !s.pincode) {
            return swal("Error", "Complete shipping address is required", "error");
        }

        try {
            setSubmitting(true);

            const fd = new FormData();
            fd.append("name", finalForm.title);
            fd.append("countryCode", finalForm.countryCode || "+91");
            fd.append("mobile", finalForm.mobile);
            fd.append("gmail", finalForm.gmail);
            fd.append("billingAddress", JSON.stringify(finalForm.billingAddress));
            fd.append("shippingAddress", JSON.stringify(finalForm.shippingAddress));
            fd.append("gstDetails", JSON.stringify(finalForm.gstDetails));
            fd.append("contactPersons", JSON.stringify(finalForm.contactPersons));

            if (file) fd.append("image", file);

            const res = await api.post("/clients", fd);
            if (!res.data.success) {
                return swal(
                    "Error",
                    res.data.message || "Something went wrong",
                    "error",
                );
            }
            if (res.data.success) {
                swal("Success", "Client added successfully!", "success");
                navigate("/clients");
            }
        } catch (err) {
            const message = err?.response?.data?.message || "Something went wrong";

            swal("Error", message, "error");
        } finally {
            setSubmitting(false);
        }
    };
    const addContactPerson = () => {
        setForm((prev) => ({
            ...prev,
            contactPersons: [
                ...prev.contactPersons,
                { name: "", countryCode: "+91", mobile: "", email: "", designation: "", whatsappTriggers: [] },
            ],
        }));
    };
    const removeContactPerson = (index) => {
        setForm((prev) => ({
            ...prev,
            contactPersons: prev.contactPersons.filter((_, i) => i !== index),
        }));
    };
    const handleContactChange = (index, field, value) => {
        setForm((prev) => {
            const updated = [...prev.contactPersons];
            updated[index][field] = value;
            return { ...prev, contactPersons: updated };
        });
    };
    const handleImageChange = (e) => {
        const selectedFile = e.target.files[0];

        if (!selectedFile) return;

        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

        if (!allowedTypes.includes(selectedFile.type)) {
            swal(
                "Invalid File",
                "Only JPG, JPEG, PNG or GIF images are allowed",
                "error",
            );
            e.target.value = "";
            return;
        }

        // Optional size limit (2MB)
        const maxSize = 2 * 1024 * 1024;

        if (selectedFile.size > maxSize) {
            swal("File Too Large", "Image must be smaller than 2MB", "error");
            e.target.value = "";
            return;
        }

        setFile(selectedFile);
    };
    return (
        <div className="container-fluid">
            <Card className="shadow-sm">
                <Card.Header>
                    <h4 className="mb-0">Add New Client</h4>
                </Card.Header>

                <Card.Body>
                    <form onSubmit={handleSubmit}>
                        {/* IMAGE + BASIC DETAILS */}
                        <Row>
                            <Col md={3} className="text-center">
                                <label className="fw-bold mb-2">Client Image</label>
                                <UserAvatar
                                    src={file ? URL.createObjectURL(file) : null}
                                    size={120}
                                    alt="client"
                                />
                                <input
                                    type="file"
                                    className="form-control mt-3"
                                    accept="image/png,image/jpeg,image/jpg,image/gif"
                                    onChange={handleImageChange}
                                />
                            </Col>

                            <Col md={9}>
                                <Row>
                                    <Col md={6}>
                                        <label className="fw-bold">Client / Company Name *</label>
                                        <input
                                            className="form-control"
                                            name="title"
                                            onChange={handleChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <label className="fw-bold">Mobile *</label>
                                        <PhoneInputWithCountry
                                            countryCode={form.countryCode || "+91"}
                                            onCountryCodeChange={(code) =>
                                                setForm((prev) => ({ ...prev, countryCode: code }))
                                            }
                                            phone={form.mobile}
                                            onPhoneChange={(val) =>
                                                setForm((prev) => ({ ...prev, mobile: val }))
                                            }
                                            placeholder="Mobile number"
                                            name="mobile"
                                        />
                                    </Col>
                                </Row>

                                <Row className="mt-3">
                                    <Col md={12}>
                                        <label className="fw-bold">Email *</label>
                                        <input
                                            className="form-control"
                                            name="gmail"
                                            onChange={handleChange}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        {/* TAX DETAILS */}
                        <h5 className="text-primary fw-bold">Tax Details</h5>
                        <Row>
                            <Col md={4}>
                                <input
                                    className="form-control"
                                    placeholder="GST Number *"
                                    name="gstDetails.gstNumber"
                                    maxLength="15"
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={4}>
                                <input
                                    className="form-control"
                                    placeholder="PAN Number"
                                    name="gstDetails.panNumber"
                                    maxLength="10"
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={4}>
                                <input
                                    className="form-control"
                                    placeholder="CIN Number"
                                    name="gstDetails.cinNumber"
                                    maxLength="21"
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        {/* BILLING ADDRESS */}
                        <h5 className="text-primary fw-bold">Billing Address</h5>
                        <Row>
                            <Col md={6}>
                                <input
                                    className="form-control"
                                    name="billingAddress.addressLine1"
                                    value={form.billingAddress.addressLine1}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="City"
                                    name="billingAddress.city"
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="State"
                                    name="billingAddress.state"
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="Pincode"
                                    name="billingAddress.pincode"
                                    maxLength="6"
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "");
                                        handleChange({ target: { name: e.target.name, value } });
                                    }}
                                />
                            </Col>
                        </Row>

                        <div className="form-check mt-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={sameAsBilling}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setSameAsBilling(checked);

                                    setForm((prev) => ({
                                        ...prev,
                                        shippingAddress: checked
                                            ? { ...prev.billingAddress }
                                            : { addressLine1: "", city: "", state: "", pincode: "" },
                                    }));
                                }}
                            />

                            <label className="form-check-label">
                                Shipping address same as billing
                            </label>
                        </div>

                        <hr className="my-4" />

                        {/* SHIPPING ADDRESS */}
                        <h5 className="text-primary fw-bold">Shipping Address</h5>
                        <Row>
                            <Col md={6}>
                                <input
                                    className="form-control"
                                    placeholder="Address Line 1"
                                    name="shippingAddress.addressLine1"
                                    value={form.shippingAddress.addressLine1}
                                    disabled={sameAsBilling}
                                    onChange={handleChange}
                                />
                            </Col>

                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="City"
                                    name="shippingAddress.city"
                                    value={form.shippingAddress.city}
                                    disabled={sameAsBilling}
                                    onChange={handleChange}
                                />
                            </Col>

                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="State"
                                    name="shippingAddress.state"
                                    value={form.shippingAddress.state}
                                    disabled={sameAsBilling}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={3}>
                                <input
                                    className="form-control"
                                    placeholder="Pincode"
                                    name="shippingAddress.pincode"
                                    maxLength="6"
                                    value={form.shippingAddress.pincode}
                                    disabled={sameAsBilling}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, "");
                                        handleChange({ target: { name: e.target.name, value } });
                                    }}
                                />
                            </Col>
                        </Row>

                        <hr className="my-4" />

                        {/* CONTACT PERSONS */}
                        <h5 className="text-primary fw-bold">Contact Person Details</h5>

                        {form.contactPersons.map((person, index) => (
                            <div key={index} className="border rounded p-3 mb-3">
                                <Row className="g-2 align-items-end">
                                    <Col md={2}>
                                        <label className="small fw-semibold text-muted mb-1">Name</label>
                                        <input
                                            className="form-control"
                                            placeholder="Name"
                                            value={person.name}
                                            onChange={(e) =>
                                                handleContactChange(index, "name", e.target.value)
                                            }
                                        />
                                    </Col>

                                    <Col md={3}>
                                        <label className="small fw-semibold text-muted mb-1">Mobile</label>
                                        <PhoneInputWithCountry
                                            countryCode={person.countryCode || "+91"}
                                            onCountryCodeChange={(code) =>
                                                handleContactChange(index, "countryCode", code)
                                            }
                                            phone={person.mobile}
                                            onPhoneChange={(val) =>
                                                handleContactChange(index, "mobile", val)
                                            }
                                            placeholder="Mobile"
                                            name="mobile"
                                        />
                                    </Col>

                                    <Col md={2}>
                                        <label className="small fw-semibold text-muted mb-1">Email</label>
                                        <input
                                            className="form-control"
                                            placeholder="Email"
                                            value={person.email}
                                            onChange={(e) =>
                                                handleContactChange(index, "email", e.target.value)
                                            }
                                        />
                                    </Col>

                                    <Col md={2}>
                                        <label className="small fw-semibold text-muted mb-1">Designation</label>
                                        <input
                                            className="form-control"
                                            placeholder="Designation"
                                            value={person.designation}
                                            onChange={(e) =>
                                                handleContactChange(
                                                    index,
                                                    "designation",
                                                    e.target.value,
                                                )
                                            }
                                        />
                                    </Col>

                                    <Col md={3}>
                                        <label className="small fw-semibold text-muted mb-1">WhatsApp</label>
                                        <Select
                                            isMulti
                                            options={WHATSAPP_TRIGGERS.map((t) => ({ value: t.key, label: t.label }))}
                                            value={WHATSAPP_TRIGGERS
                                                .filter((t) => (person.whatsappTriggers || []).includes(t.key))
                                                .map((t) => ({ value: t.key, label: t.label }))}
                                            onChange={(selected) =>
                                                handleContactChange(
                                                    index,
                                                    "whatsappTriggers",
                                                    (selected || []).map((s) => s.value)
                                                )
                                            }
                                            placeholder="Triggers..."
                                            classNamePrefix="react-select"
                                        />
                                    </Col>
                                </Row>

                                {form.contactPersons.length > 1 && (
                                    <div className="text-end mt-2">
                                        <Button
                                            type="button" // REQUIRED
                                            variant="danger"
                                            size="sm"
                                            onClick={() => removeContactPerson(index)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}

                        <Button
                            type="button" // REQUIRED
                            variant="outline-primary"
                            onClick={addContactPerson}
                        >
                            + Add Another Contact
                        </Button>

                        <div className="text-end mt-4">
                            <Button type="submit" disabled={submitting}>
                                {submitting ? "Adding Client..." : "Add Client"}
                            </Button>
                        </div>
                    </form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AddClient;
